import React, { useEffect, useState, useMemo, useRef } from 'react';
import './Dashboard.css';
import { useLazyQuery } from '@apollo/client';
import { DashboardListReports, getDeletedUserHistory } from '../../graphql/queries';
import { Pie, Bar } from 'react-chartjs-2';
import useFetchUserByEmail from '../../components/UserInfoLoader/LoadUserInfo';
import TexasMap from "../../components/map/TexasMap";
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import ZipcodeMapModal from "./components/ZipCodeMapModal";
import * as XLSX from 'xlsx';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const Dashboard = () => {
  // --------------------------------------
  // State declarations
  // --------------------------------------
  const [weekNumber, setWeekNumber] = useState(0);
  const [py, setPy] = useState(1); // Starting with PY 1
  const [quarterNumber, setQuarterNumber] = useState(1);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [totalQHPAmount, setTotalQHPAmount] = useState(0);
  const [medicaidCount, setMedicaidCount] = useState(0);
  const [clientEnrollYesCount, setClientEnrollYesCount] = useState(0);
  const [qhpEnrollmentsByMonth, setQhpEnrollmentsByMonth] = useState(Array(12).fill(0));
  const [medicaidEnrollmentsByMonth, setMedicaidEnrollmentsByMonth] = useState(Array(12).fill(0));
  const [currentUserId, setCurrentUserId] = useState(null);
  const [overallProgressPercentage, setOverallProgressPercentage] = useState(0);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const { userInfo: userLoggedIn } = useFetchUserByEmail(currentUserId);
  const [deletedUsers, setDeletedUsers] = useState({});
  const [userGroups, setUserGroups] = useState([]);
  const hasGroup = (group) => userGroups.includes(group);

  // "QHP" or "Medicaid" (for the map)
  const [mapMode, setMapMode] = useState('QHP');

  // Date range for main (yearly) filter
  const startDate = '2025-08-27';
  const endDate = '2026-08-29';

  // Date filters for the map
  const [mapStartDate, setMapStartDate] = useState('2025-08-27');
  const [mapEndDate, setMapEndDate] = useState('2026-08-29');

  // Pie chart states
  const [enrollmentsByUser, setEnrollmentsByUser] = useState({});
  const [outreachData, setOutreachData] = useState({ yes: 0, no: 0 });
  const [totalEnrollments, setTotalEnrollments] = useState(0);

  // Modal state
  const [isZipcodeMapModalOpen, setIsZipcodeMapModalOpen] = useState(false);
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [zipcodeData, setZipcodeData] = useState([]);

  // Weekly & yearly goals states
  const expectedQHP_yr = 190;
  const expectedQHP_wk = 10;
  const [currentQHP_yr, setCurrentQHP_yr] = useState(0);
  const [currentQHP_week, setCurrentQHP_week] = useState(0);

  const expectedMedicaidChip_yr = 100;
  const expectedMedicaidChip_wk = 3;
  const [currentMedicaidChip_yr, setCurrentMedicaid_yr] = useState(0);
  const [currentMedicaidChip_week, setCurrentMedicaid_week] = useState(0);

  const expected_one2one_yr = 16666;
  const expected_one2one_wk = 320;
  const [current_one2one_yr, setCurrent_one2one_yr] = useState(0);
  const [current_one2one_week, setCurrent_one2one_week] = useState(0);

  const expected_health_lit_edu_yr = 555;
  const expected_health_lit_edu_wk = 11;
  const [current_health_lit_edu_yr, setCurrent_health_lit_edu_yr] = useState(0);
  const [current_health_lit_edu_week, setCurrent_health_lit_edu_week] = useState(0);

  const expected_post_enrollment_asst_enroll_iss_complex_cases_yr = 250;
  const expected_post_enrollment_asst_enroll_iss_complex_cases_wk = 5;
  const [current_post_enrollment_asst_enroll_iss_complex_cases_yr, setCurrent_post_enrollment_asst_enroll_iss_complex_cases_yr] = useState(0);
  const [current_post_enrollment_asst_enroll_iss_complex_cases_week, setCurrent_post_enrollment_asst_enroll_iss_complex_cases_week] = useState(0);

  const expected_tax_forms_appeals_yr = 694;
  const expected_tax_forms_appeals_wk = 13;
  const [current_tax_forms_appeals_yr, setCurrent_tax_forms_appeals_yr] = useState(0);
  const [current_tax_forms_appeals_week, setCurrent_tax_forms_appeals_week] = useState(0);

  const navigate = useNavigate();
  const handleGoToRankDashboard = () => {
    navigate('/rank-dashboard');
  };

  // Prepare arrays to map weekly/yearly goals in two columns
  const weeklyGoals = [
    { name: "QHP", current: currentQHP_week, expected: expectedQHP_wk },
    { name: "Medicaid/CHIP", current: currentMedicaidChip_week, expected: expectedMedicaidChip_wk },
    { name: "One-to-One", current: current_one2one_week, expected: expected_one2one_wk },
    { name: "Health Literacy Education", current: current_health_lit_edu_week, expected: expected_health_lit_edu_wk },
    { name: "Post-Enrollment Assistance", current: current_post_enrollment_asst_enroll_iss_complex_cases_week, expected: expected_post_enrollment_asst_enroll_iss_complex_cases_wk },
    { name: "Tax Forms & Appeals", current: current_tax_forms_appeals_week, expected: expected_tax_forms_appeals_wk }
  ];

  const yearlyGoals = [
    { name: "QHP", current: currentQHP_yr, expected: expectedQHP_yr },
    { name: "Medicaid/CHIP", current: currentMedicaidChip_yr, expected: expectedMedicaidChip_yr },
    { name: "One-to-One", current: current_one2one_yr, expected: expected_one2one_yr },
    { name: "Health Literacy Education", current: current_health_lit_edu_yr, expected: expected_health_lit_edu_yr },
    { name: "Post-Enrollment Assistance", current: current_post_enrollment_asst_enroll_iss_complex_cases_yr, expected: expected_post_enrollment_asst_enroll_iss_complex_cases_yr },
    { name: "Tax Forms & Appeals", current: current_tax_forms_appeals_yr, expected: expected_tax_forms_appeals_yr }
  ];

  // Chart data for "Reports by Users" pie
  const pieChartData = {
    labels: ['Your Reports', 'Others'],
    datasets: [
      {
        data: [enrollmentsByUser['Your Enrollments'], enrollmentsByUser['Others']],
        backgroundColor: ['rgba(39, 126, 223, 0.8)', 'rgba(44, 79, 163, 0.8)'],
        borderColor: ['rgba(39, 126, 223, 1)', 'rgba(44, 79, 163, 1)'],
        borderWidth: 1,
      },
    ],
  };

  // Chart data for "Outreach Events" pie
  const outreachPieData = {
    labels: ['Outreach - Yes', 'Outreach - No'],
    datasets: [
      {
        data: [outreachData.yes, outreachData.no],
        backgroundColor: ['rgba(44, 79, 163, 0.8)', 'rgba(39, 126, 223, 0.8)'],
        borderColor: ['rgba(44, 79, 163, 1)', 'rgba(39, 126, 223, 1)'],
        borderWidth: 1,
      },
    ],
  };

  // useLazyQuery to fetch pages
  const [fetchReports] = useLazyQuery(DashboardListReports, {
    fetchPolicy: 'cache-first',
  });
  const [fetchDeletedUser] = useLazyQuery(getDeletedUserHistory);


  /**
   * -------------------------------------------
   * Fetch all pages in parallel (worker pool)
   * -------------------------------------------
   */
  const loadAllReportsInParallel = async (concurrency = 5) => {
    const uniqueItemsMap = new Map();
    let queue = [null];
    let activeWorkers = 0;
    let done = false;

    async function worker() {
      while (!done) {
        let token;
        if (queue.length > 0) {
          token = queue.shift();
        } else {
          break;
        }

        activeWorkers++;
        try {
          const { data } = await fetchReports({ variables: { limit: 500, nextToken: token } });
          if (data?.listReportFixes) {
            const { items, nextToken: newToken } = data.listReportFixes;
            for (const item of items) {
              if (!uniqueItemsMap.has(item.id)) {
                uniqueItemsMap.set(item.id, item);
              }
            }
            if (newToken) {
              queue.push(newToken);
            }
            const currentAll = Array.from(uniqueItemsMap.values());
            setReports(currentAll);
          }
        } catch (err) {
          console.error('Error fetching page:', err);
        } finally {
          activeWorkers--;
        }
      }
    }

    const workers = [];
    for (let i = 0; i < concurrency; i++) {
      workers.push(worker());
    }

    while (!(queue.length === 0 && activeWorkers === 0)) {
      await new Promise(res => setTimeout(res, 200));
    }

    done = true;
    await Promise.all(workers);
    setLoading(false);
  };

  // Kick off the parallel loading once on mount
  useEffect(() => {
    loadAllReportsInParallel(3);
  }, []);

  // Fetch the current user
  const fetchCurrentUser = async () => {
    try {
      const { signInDetails } = await getCurrentUser();
      const session = await fetchAuthSession();
      const groups = session.tokens.idToken.payload['cognito:groups'] || [];
      setUserGroups(groups);
      setCurrentUserId(signInDetails.loginId);
    } catch (error) {
      console.error('Failed to fetch current user:', error);
    }
  };

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  // Calculate the current "week" range
  const calculateWeeklyRange = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0=Sun, 6=Sat
    const daysSinceFriday = (dayOfWeek + 2) % 7;
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - daysSinceFriday);
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    return { startOfWeek, endOfWeek };
  };

  // -------------------------------------------
  // Recalculate derived values on data changes
  // -------------------------------------------
  useEffect(() => {
    if (!reports.length || !currentUserId) return;

    const { startOfWeek, endOfWeek } = calculateWeeklyRange();
    const weeklyReports = reports.filter(item => {
      const [year, month, day] = item.Date.split('-').map(Number);
      const itemDate = new Date(year, month - 1, day);
      return itemDate >= startOfWeek && itemDate <= endOfWeek;
    });

    // Filter for big date range
    const filteredReports = reports.filter(item => {
      return item.Date >= startDate && item.Date <= endDate;
    });

    // Sum QHP for entire (filtered) year range
    const totalQHP = filteredReports.reduce((sum, item) => {
      const fieldsToSum = [
        'qhp_enrollment_hcgov',
        'elctronic_qhp_enrollment_marketplaceCallCenter',
        'written_qhp_enrollment_hcgov',
      ];
      const amount = fieldsToSum.reduce((fieldSum, field) => {
        const value = parseFloat(item[field]);
        return !isNaN(value) ? fieldSum + value : fieldSum;
      }, 0);
      return sum + amount;
    }, 0);
    setTotalQHPAmount(totalQHP);

    // QHP for current user
    const userQHPAmount = filteredReports.reduce((sum, item) => {
      if (item.user?.id === userLoggedIn?.id) {
        const fieldsToSum = [
          'qhp_enrollment_hcgov',
          'elctronic_qhp_enrollment_marketplaceCallCenter',
          'written_qhp_enrollment_hcgov',
        ];
        const amount = fieldsToSum.reduce((fieldSum, field) => {
          const value = parseFloat(item[field]);
          return !isNaN(value) ? fieldSum + value : fieldSum;
        }, 0);
        return sum + amount;
      }
      return sum;
    }, 0);
    setCurrentQHP_yr(userQHPAmount);

    // Weekly QHP for current user
    const userQHPWeeklyAmount = weeklyReports.reduce((sum, item) => {
      if (item.Username === currentUserId) {
        const fieldsToSum = [
          'qhp_enrollment_hcgov',
          'elctronic_qhp_enrollment_marketplaceCallCenter',
          'written_qhp_enrollment_hcgov',
        ];
        const value = fieldsToSum.reduce((fieldSum, field) => {
          const v = parseFloat(item[field]);
          return !isNaN(v) ? fieldSum + v : fieldSum;
        }, 0);
        return sum + value;
      }
      return sum;
    }, 0);
    setCurrentQHP_week(userQHPWeeklyAmount);

    // Medicaid across all
    const totalMedicaid = filteredReports.reduce((sum, item) => {
      const amount = parseFloat(item.medicaid_chip_app_ref);
      return !isNaN(amount) ? sum + amount : sum;
    }, 0);
    setMedicaidCount(totalMedicaid);

    // Medicaid for current user
    const userMedicaidAmount = filteredReports.reduce((sum, item) => {
      if (item.Username === currentUserId) {
        const amount = parseFloat(item.medicaid_chip_app_ref);
        return !isNaN(amount) ? sum + amount : sum;
      }
      return sum;
    }, 0);
    setCurrentMedicaid_yr(userMedicaidAmount);

    // Weekly Medicaid for user
    const userMedicaidWeeklyAmount = weeklyReports.reduce((sum, item) => {
      if (item.Username === currentUserId) {
        const amount = parseFloat(item.medicaid_chip_app_ref);
        return !isNaN(amount) ? sum + amount : sum;
      }
      return sum;
    }, 0);
    setCurrentMedicaid_week(userMedicaidWeeklyAmount);

    // ClientEnroll "Yes" total
    const clientEnrollCount = filteredReports.reduce((count, item) => {
      return item.clientEnroll === 'Yes' ? count + 1 : count;
    }, 0);
    setClientEnrollYesCount(clientEnrollCount);

    // Outreach & "Reports by Users"
    let userEnrollCount = 0;
    let otherEnrollCount = 0;
    let outreachYes = 0;
    let outreachNo = 0;

    filteredReports.forEach(item => {
      if (item.Username === currentUserId) {
        userEnrollCount += 1;
      } else {
        otherEnrollCount += 1;
      }
      const outreachValue = item.outreachEvent?.trim().toLowerCase();
      if (outreachValue === 'yes') {
        outreachYes += 1;
      } else if (outreachValue === 'no') {
        outreachNo += 1;
      }
    });
    setOutreachData({ yes: outreachYes, no: outreachNo });
    setEnrollmentsByUser({
      'Your Enrollments': userEnrollCount,
      'Others': otherEnrollCount,
    });
    setTotalEnrollments(userEnrollCount + otherEnrollCount);

    // Enrollments by Month
    const qhpByMonth = Array(12).fill(0);
    const medicaidByMonth = Array(12).fill(0);

    filteredReports.forEach(item => {
      const [year, month, day] = item.Date.split('-').map(Number);
      // For actual usage, you could do month-1. We'll keep as is for clarity
      const itemDate = new Date(year, month - 1, day);

      const qhpValue = parseFloat(item.qhp_enrollment_hcgov);
      if (!isNaN(qhpValue)) {
        qhpByMonth[month - 1] += qhpValue;
      }
      const medicaidValue = parseFloat(item.medicaid_chip_app_ref);
      if (!isNaN(medicaidValue)) {
        medicaidByMonth[month - 1] += medicaidValue;
      }
    });
    setQhpEnrollmentsByMonth(qhpByMonth);
    setMedicaidEnrollmentsByMonth(medicaidByMonth);
    // === One-to-One ===
    const totalOne2OneYr = filteredReports.reduce((sum, item) => {
      if (item.Username === currentUserId) {
        const value = parseFloat(item.one2oneInquiry) || 0;
        return sum + value;
      }
      return sum;
    }, 0);
    setCurrent_one2one_yr(totalOne2OneYr);

    const weeklyOne2One = weeklyReports.reduce((sum, item) => {
      if (item.Username === currentUserId) {
        const value = parseFloat(item.one2oneInquiry) || 0;
        return sum + value;
      }
      return sum;
    }, 0);
    setCurrent_one2one_week(weeklyOne2One);

    // === Health Literacy Education ===
    const totalHealthEduYr = filteredReports.reduce((sum, item) => {
      if (item.Username === currentUserId) {
        const value = parseFloat(item.HealthInsuranceLiteracy) || 0;
        return sum + value;
      }
      return sum;
    }, 0);
    setCurrent_health_lit_edu_yr(totalHealthEduYr);

    const weeklyHealthEdu = weeklyReports.reduce((sum, item) => {
      if (item.Username === currentUserId) {
        const value = parseFloat(item.HealthInsuranceLiteracy) || 0;
        return sum + value;
      }
      return sum;
    }, 0);
    setCurrent_health_lit_edu_week(weeklyHealthEdu);

    // === Post-Enrollment Assistance (Updated) ===
    const totalPostEnrollYr = filteredReports.reduce((sum, item) => {
      if (item.Username === currentUserId) {
        const fields = [
          item.complex_case_help_center,
          item.data_matchingIssues,
          item.SEP_eligibility,
          item.Employer_sponsored_coverage_issues,
          item.APTC_CSR_assistance,
        ];

        const total = fields.reduce((fieldSum, val) => {
          const value = val === 'None' ? 0 : parseFloat(val);
          return fieldSum + (isNaN(value) ? 0 : value);
        }, 0);

        return sum + total;
      }
      return sum;
    }, 0);
    setCurrent_post_enrollment_asst_enroll_iss_complex_cases_yr(totalPostEnrollYr);

    const weeklyPostEnroll = weeklyReports.reduce((sum, item) => {
      if (item.Username === currentUserId) {
        const fields = [
          item.complex_case_help_center,
          item.data_matchingIssues,
          item.SEP_eligibility,
          item.Employer_sponsored_coverage_issues,
          item.APTC_CSR_assistance,
        ];

        const total = fields.reduce((fieldSum, val) => {
          const value = val === 'None' ? 0 : parseFloat(val);
          return fieldSum + (isNaN(value) ? 0 : value);
        }, 0);

        return sum + total;
      }
      return sum;
    }, 0);
    setCurrent_post_enrollment_asst_enroll_iss_complex_cases_week(weeklyPostEnroll);

    // === Tax Forms & Appeals ===
    const totalTaxFormsYr = filteredReports.reduce((sum, item) => {
      if (item.Username === currentUserId) {
        const value1 = parseFloat(item.Marketplace_tax_forms) || 0;
        const value2 = parseFloat(item.Filing_Marketplace) || 0;
        const value3 = parseFloat(item.Submitting_Marketplace) || 0;
        return sum + value1 + value2 + value3;
      }
      return sum;
    }, 0);
    setCurrent_tax_forms_appeals_yr(totalTaxFormsYr);

    const weeklyTaxForms = weeklyReports.reduce((sum, item) => {
      if (item.Username === currentUserId) {
        const value1 = parseFloat(item.Marketplace_tax_forms) || 0;
        const value2 = parseFloat(item.Filing_Marketplace) || 0;
        const value3 = parseFloat(item.Submitting_Marketplace) || 0;
        return sum + value1 + value2 + value3;
      }
      return sum;
    }, 0);
    setCurrent_tax_forms_appeals_week(weeklyTaxForms);

  }, [reports, currentUserId, userLoggedIn]);

  // Calculate week, PY, etc.
  useEffect(() => {
    const calculateWeekAndPY = () => {
      const startOfPY1 = new Date('2024-08-30');
      const currentDate = new Date();
      const msInWeek = 7 * 24 * 60 * 60 * 1000;
      const diffInMs = currentDate - startOfPY1;
      const weeksPassed = Math.floor(diffInMs / msInWeek);

      setWeekNumber(weeksPassed + 1);
      const currentPY = Math.floor(weeksPassed / 52) + 1;
      setPy(currentPY);
      const currentQuarter = Math.floor((weeksPassed % 52) / 13) + 1;
      setQuarterNumber(currentQuarter);

      const weeksIntoCurrentPY = weeksPassed % 52;
      const progress = (weeksIntoCurrentPY / 52) * 100;
      setProgressPercentage(progress);
    };
    calculateWeekAndPY();
  }, []);

  // Overall QHP progress
  useEffect(() => {
    const overallTargetQHPs = 3400;
    const progress = (totalQHPAmount / overallTargetQHPs) * 100;
    setOverallProgressPercentage(progress);  // <-- don't limit here
  }, [totalQHPAmount]);

  const normalizeCounty = (county) => {
    if (!county) return "Unknown County";
    // Trim and convert to lowercase
    let norm = county.trim().toLowerCase();
    // Remove any trailing " county" (ignoring case)
    norm = norm.replace(/\s*county\s*$/i, '');
    // Split on one or more spaces, then capitalize each word
    norm = norm
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    // Append " County" to ensure consistency
    return norm + " County";
  };

  // Build county data for the map
  const countyData = useMemo(() => {
    if (!mapStartDate || !mapEndDate || !reports.length) return {};

    const parseLocalDate = (dateString) => {
      const [year, month, day] = dateString.split("-").map(Number);
      return new Date(year, month - 1, day, 0, 0, 0, 0);
    };
    const start = parseLocalDate(mapStartDate);
    const end = parseLocalDate(mapEndDate);
    end.setHours(23, 59, 59, 999);

    const countyAggregator = {};

    for (const report of reports) {
      const [year, month, day] = report.Date.split('-').map(Number);
      const reportDate = new Date(year, month - 1, day);

      if (reportDate >= start && reportDate <= end) {
        const originalCounty = report.county || "Unknown";
        const normalizedCounty = normalizeCounty(originalCounty);

        let total = 0;
        if (mapMode === 'QHP') {
          ['qhp_enrollment_hcgov', 'elctronic_qhp_enrollment_marketplaceCallCenter', 'written_qhp_enrollment_hcgov']
            .forEach(field => {
              const val = parseFloat(report[field]);
              if (!isNaN(val)) total += val;
            });
        } else {
          total = parseFloat(report.medicaid_chip_app_ref) || 0;
        }

        if (!countyAggregator[normalizedCounty]) {
          countyAggregator[normalizedCounty] = 0;
        }
        countyAggregator[normalizedCounty] += total;
      }
    }

    Object.keys(countyAggregator).forEach(county => {
      countyAggregator[county] = Math.round(countyAggregator[county]);
    });

    return countyAggregator;
  }, [reports, mapStartDate, mapEndDate, mapMode]);


  // Bar chart data
  const chartData = {
    labels: [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ],
    datasets: [
      {
        label: 'QHP Enrollments',
        data: qhpEnrollmentsByMonth,
        backgroundColor: 'rgba(44, 79, 163, 0.8)',
        borderColor: 'rgba(44, 79, 163, 1)',
        borderWidth: 1,
      },
      {
        label: 'Medicaid Enrollments',
        data: medicaidEnrollmentsByMonth,
        backgroundColor: 'rgba(39, 126, 223, 0.8)',
        borderColor: 'rgba(39, 126, 223, 1)',
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(200, 200, 200, 0.2)',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: {
          size: 14,
        },
        bodyFont: {
          size: 13,
        },
        padding: 10,
        cornerRadius: 6,
        displayColors: true,
      },
    },
  };

  const getZipcodeDataByCounty = (countyName, mode) => {
    // Use the helper to normalize the county name
    const normalizedCounty = normalizeCounty(countyName);

    // Filter reports using the normalized value.
    const relevantReports = reports.filter(rep => {
      // Normalize the report's county name as well.
      const repCounty = normalizeCounty(rep.county || '');
      // Also, check that the report date is within the map range.
      const parseLocalDate = (dateString) => {
        const [year, month, day] = dateString.split("-").map(Number);
        return new Date(year, month - 1, day, 0, 0, 0, 0);
      };
      const start = parseLocalDate(mapStartDate);
      const end = parseLocalDate(mapEndDate);
      end.setHours(23, 59, 59, 999);
      const reportDate = parseLocalDate(rep.Date);

      return repCounty === normalizedCounty && reportDate >= start && reportDate <= end;
    });

    const aggregator = {};
    for (const rep of relevantReports) {
      if (!rep.zip_code) continue;
      let zip = rep.zip_code.toString().trim();
      zip = zip.replace(/\.0$/, '');
      if (!/^\d{5}$/.test(zip)) continue;
      let amt = 0;
      if (mode === 'QHP') {
        const q1 = parseFloat(rep.qhp_enrollment_hcgov) || 0;
        const q2 = parseFloat(rep.elctronic_qhp_enrollment_marketplaceCallCenter) || 0;
        const q3 = parseFloat(rep.written_qhp_enrollment_hcgov) || 0;
        amt = q1 + q2 + q3;
      } else {
        amt = parseFloat(rep.medicaid_chip_app_ref) || 0;
      }
      aggregator[zip] = (aggregator[zip] || 0) + amt;
    }

    const result = Object.entries(aggregator)
      .filter(([_, total]) => total > 0)
      .map(([zip, total]) => ({
        zip,
        total: Math.round(total),
      }));

    return result;
  };

  // ----------------------------
  // Callback: county clicked
  // ----------------------------
  const handleCountyClick = (countyName) => {
    if (!countyName) return;
    setSelectedCounty(countyName);
    setZipcodeData([]);
    // Build the ZIP code data for that county
    const zipData = getZipcodeDataByCounty(countyName, mapMode);
    setZipcodeData(zipData);

    // Open the modal
    setIsZipcodeMapModalOpen(true);
  };

  // ----------------------------
  // Close modal
  // ----------------------------
  const handleCloseModal = () => {
    setIsZipcodeMapModalOpen(false);
  };

  // ----------------------------
  // Download data from map to excel
  // ----------------------------

  const handleDownloadMapData = async () => {
    const parseLocalDate = (dateString) => {
      const [year, month, day] = dateString.split("-").map(Number);
      return new Date(year, month - 1, day);
    };

    const start = parseLocalDate(startDate); // Match dashboard filter
    const end = parseLocalDate(endDate);
    end.setHours(23, 59, 59, 999);

    const summary = { QHP: 0, Medicaid: 0 };

    const byZip = {};
    const byCounty = {};
    const byCity = {};
    const byNavigator = {};

    const deletedUsersCache = new Map();

    const resolveNavigatorName = async (user, userId, fallback) => {
      // Use existing user's name if present
      if (user?.first_name) return user.first_name + ' ' + user.last_name;

      const id = user?.id || userId;
      if (!id) return fallback;

      try {
        const res = await fetchDeletedUser({ variables: { id } });
        const deleted = res.data?.getDeletedUserHistory;
        if (deleted) {
          setDeletedUsers(prev => ({ ...prev, [id]: deleted }));
          return deleted.first_name + ' ' + deleted.last_name;
        }
      } catch (err) {
        console.error(`Failed to fetch deleted user for ${id}`, err);
      }

      return fallback;
    };

    for (const report of reports) {
      const reportDate = parseLocalDate(report.Date);
      if (reportDate < start || reportDate > end) continue;

      const zip = (report.zip_code || "").toString().trim().replace(/\.0$/, '');
      const county = normalizeCounty(report.county || "Unknown");
      const city = report.city?.trim() || "Unknown";

      const qhp =
        (parseFloat(report.qhp_enrollment_hcgov) || 0) +
        (parseFloat(report.elctronic_qhp_enrollment_marketplaceCallCenter) || 0) +
        (parseFloat(report.written_qhp_enrollment_hcgov) || 0);

      const medicaid = parseFloat(report.medicaid_chip_app_ref) || 0;
      const total = qhp + medicaid;

      summary.QHP += qhp;
      summary.Medicaid += medicaid;

      if (/^\d{5}$/.test(zip)) {
        if (!byZip[zip]) byZip[zip] = { qhp: 0, medicaid: 0 };
        byZip[zip].qhp += qhp;
        byZip[zip].medicaid += medicaid;
      }

      if (!byCounty[county]) byCounty[county] = { qhp: 0, medicaid: 0 };
      byCounty[county].qhp += qhp;
      byCounty[county].medicaid += medicaid;

      if (!byCity[city]) byCity[city] = { qhp: 0, medicaid: 0 };
      byCity[city].qhp += qhp;
      byCity[city].medicaid += medicaid;

      const navigatorName = await resolveNavigatorName(report.user, report.userId, 'Unknown');
      if (!byNavigator[navigatorName]) byNavigator[navigatorName] = { qhp: 0, medicaid: 0 };
      byNavigator[navigatorName].qhp += qhp;
      byNavigator[navigatorName].medicaid += medicaid;
    }

    const toSheetData = (dataObj, label) =>
      Object.entries(dataObj).map(([key, value]) => ({
        [label]: key,
        QHP: Math.round(value.qhp),
        Medicaid: Math.round(value.medicaid),
        Total: Math.round(value.qhp + value.medicaid),
      }));

    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet([{
        QHP: Math.round(summary.QHP),
        Medicaid: Math.round(summary.Medicaid),
        Total: Math.round(summary.QHP + summary.Medicaid),
      }]),
      'Enrollments'
    );

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(toSheetData(byZip, 'ZipCode')), 'By Zip Code');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(toSheetData(byCounty, 'County')), 'By County');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(toSheetData(byCity, 'City')), 'By City');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(toSheetData(byNavigator, 'Navigator')), 'By Navigator');

    XLSX.writeFile(wb, 'Detailed_Enrollment_Report.xlsx');
  };

  // --------------------------------------
  // Render
  // --------------------------------------
  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1>Dashboard</h1>
      </header>
      {/* 3 stat cards on top, centered */}
      <div className="stats-top-centered">
        <div className="dashboard-stats">
          <div className="stat-card">
            <div className="stat-content">
              <h3>Enrollments</h3>
              <p className="stat-value">
                {loading ? (
                  <span className="loading-placeholder"></span>
                ) : (
                  Math.round(totalQHPAmount + medicaidCount)
                )}
              </p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-content">
              <h3>QHPs</h3>
              <p className="stat-value">
                {loading ? <span className="loading-placeholder"></span> : totalQHPAmount}
              </p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-content">
              <h3>Medicaids</h3>
              <p className="stat-value">
                {loading ? <span className="loading-placeholder"></span> : medicaidCount}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Full width info panel */}
      <div className="dashboard-info-panel">
        <div className="time-status">
          <span className="chip">Week {weekNumber}</span>
          <span className="chip">PY {py}</span>
          <span className="chip">Q{quarterNumber}</span>
        </div>

        <div className="progress-section">
          <div className="progress-header">
            <h3>CMS Program Year Progress</h3>
            <span className="progress-percent">{progressPercentage.toFixed(1)}%</span>
          </div>
          <div className="progress-bar">
            <div
              className="progress"
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </div>

        <div className="progress-section">
          <div className="progress-header">
            <h3>Overall QHP Progress</h3>
            <span className="progress-percent">
              {overallProgressPercentage.toFixed(1)}%
              {overallProgressPercentage > 100 && (
                <span className="extra-percent">
                  {" "} (+{(overallProgressPercentage - 100).toFixed(1)}% above)
                </span>
              )}
            </span>
          </div>
          <div className="progress-bar">
            <div
              className="progress overall-progress"
              style={{ width: `${Math.min(overallProgressPercentage, 100)}%` }}
            ></div>
          </div>
        </div>

        {/* <p className="info-text">There are no appointments or events today.</p> */}

        <button onClick={handleGoToRankDashboard} className="action-button">
          Team Ranking
        </button>
      </div>

      {/* Main content area */}
      <div className="dashboard-main">
        {/* Goals Section: Weekly (left) / Yearly (right) */}
        <div className="goals-section">
          <h2>Progress & Goals</h2>
          <div className="goals-grid">
            {/* Weekly goals column */}
            <div className="goals-column">
              <h3>Weekly Goals</h3>
              {weeklyGoals.map((goal) => {
                const percentage = goal.expected ? (goal.current / goal.expected) * 100 : 0;
                return (
                  <div key={goal.name} className="goal-item">
                    <div className="goal-header">
                      <span>{goal.name}</span>
                      <span>
                        {goal.current} / {goal.expected}
                      </span>
                    </div>
                    <div className="goal-bar">
                      <div
                        className="goal-progress"
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Yearly goals column */}
            <div className="goals-column">
              <h3>Yearly Goals</h3>
              {yearlyGoals.map((goal) => {
                const percentage = goal.expected ? (goal.current / goal.expected) * 100 : 0;
                return (
                  <div key={goal.name} className="goal-item">
                    <div className="goal-header">
                      <span>{goal.name}</span>
                      <span>
                        {goal.current} / {goal.expected}
                      </span>
                    </div>
                    <div className="goal-bar">
                      <div
                        className="goal-progress"
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="charts-grid">
          <div className="dashboard-chart-container enrollments-chart">
            <h2>Enrollments by Month ({new Date().getFullYear()})</h2>
            <div className="chart-wrapper">
              <Bar data={chartData} options={options} />
            </div>
          </div>

          <div className="pie-charts-container">
            <div className="pie-chart-section">
              <h2>Reports by Users</h2>
              <div className="pie-chart-wrapper">
                {Object.keys(enrollmentsByUser).length > 0 ? (
                  <Pie
                    data={pieChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom',
                        },
                      },
                    }}
                  />
                ) : (
                  <div className="no-data-message">No data available</div>
                )}
              </div>
            </div>

            <div className="pie-chart-section">
              <h2>Outreach Events</h2>
              <div className="pie-chart-wrapper">
                {outreachData.yes + outreachData.no > 0 ? (
                  <Pie
                    data={outreachPieData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom',
                        },
                      },
                    }}
                  />
                ) : (
                  <div className="no-data-message">No data available</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Map Section */}
        <div className="map-section">
          <div className="map-header">
            <h2>Geographic Distribution: {mapMode}</h2>

            <div className="map-controls">
              <div className="date-controls">
                <div className="date-field">
                  <label htmlFor="map-start-date">From:</label>
                  <input
                    id="map-start-date"
                    type="date"
                    value={mapStartDate}
                    onChange={e => setMapStartDate(e.target.value)}
                  />
                </div>
                <div className="date-field">
                  <label htmlFor="map-end-date">To:</label>
                  <input
                    id="map-end-date"
                    type="date"
                    value={mapEndDate}
                    onChange={e => setMapEndDate(e.target.value)}
                  />
                </div>
                {hasGroup('Admin') && (
                  <>
                    <button className="download-map-btn" onClick={handleDownloadMapData}>
                      Download County Data
                    </button>
                  </>
                )}
              </div>

              <div className="map-mode-toggle">
                <label className={`toggle-option ${mapMode === 'QHP' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="mapMode"
                    value="QHP"
                    checked={mapMode === 'QHP'}
                    onChange={() => setMapMode('QHP')}
                  />
                  <span>QHP</span>
                </label>

                <label className={`toggle-option ${mapMode === 'Medicaid' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="mapMode"
                    value="Medicaid"
                    checked={mapMode === 'Medicaid'}
                    onChange={() => setMapMode('Medicaid')}
                  />
                  <span>Medicaid</span>
                </label>
              </div>
            </div>
          </div>

          <div className="map-container">
            {/* RENDER MODAL */}
            <ZipcodeMapModal
              isOpen={isZipcodeMapModalOpen}
              onClose={handleCloseModal}
              countyName={selectedCounty}
              zipcodeData={zipcodeData}
              mapMode={mapMode}
            />
            <TexasMap
              countyData={countyData}
              mapMode={mapMode}
              zoom={2.0}
              onCountyClick={handleCountyClick}
            />
          </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard;
